import type { PlanOfferInput } from '../../types/learning-plans';

export interface OfferValidationCatalog {
  subjects: { id: string; slug: string }[];
  grades: { id: string; levelId: string }[];
  levels: { id: string; slug: string }[];
}

export function offerStepErrors(offer: PlanOfferInput, step: number, catalog?: OfferValidationCatalog): string[] {
  const errors: string[] = [];
  const integer = (value: number, min: number, max: number): boolean =>
    Number.isInteger(value) && value >= min && value <= max;
  if (step === 0) {
    if (!offer.nameEn.trim() || offer.nameEn.length > 160)
      errors.push('أدخل اسم الخطة بالإنجليزية بحد أقصى 160 حرفًا.');
    if (!offer.nameAr.trim() || offer.nameAr.length > 160)
      errors.push('أدخل اسم الخطة بالعربية بحد أقصى 160 حرفًا.');
    if (!/^[a-z][a-z0-9_]{0,79}$/.test(offer.slug))
      errors.push(
        'استخدم معرّف رابط يبدأ بحرف إنجليزي صغير، ثم أحرف أو أرقام أو شرطات سفلية (بحد أقصى 80 حرفًا).',
      );
    if (!/^[A-Z]{2}$/.test(offer.countryCode))
      errors.push('أدخل رمز الدولة من حرفين مثل SA أو EG.');
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(offer.curriculum))
      errors.push('استخدم رمز منهج يبدأ بحرف إنجليزي صغير (بحد أقصى 40 حرفًا).');
    if (catalog && offer.tracks.some((track) => track.model === 'generalist')) {
      const primary = catalog.levels.find((level) => level.slug === 'primary');
      if (offer.gradeIds.some((id) => catalog.grades.find((grade) => grade.id === id)?.levelId !== primary?.id))
        errors.push('خطط المعلّم الشامل متاحة للصفوف الابتدائية فقط.');
    }
    if (catalog && offer.gradeIds.some((id) => !catalog.grades.some((grade) => grade.id === id)))
      errors.push('اختر صفوفًا متاحة في الدولة المحددة.');
    if (!/^[A-Z]{3}$/.test(offer.currency))
      errors.push('أدخل رمز العملة من ثلاثة أحرف مثل SAR أو EGP.');
    if (offer.gradeIds.length < 1 || offer.gradeIds.length > 30)
      errors.push('اختر من 1 إلى 30 صفًا متاحًا للخطة.');
    errors.push(...offerPriceErrors(offer));
  }
  if (step === 1) {
    if (offer.tracks.length < 1 || offer.tracks.length > 20)
      errors.push('أضف من 1 إلى 20 مسارًا للدروس.');
    const keys = new Set<string>();
    for (const [index, track] of offer.tracks.entries()) {
      const label = `المسار ${index + 1}`;
      if (!/^[a-z][a-z0-9_]{0,39}$/.test(track.key))
        errors.push(
          `${label}: استخدم معرّف رابط يبدأ بحرف إنجليزي صغير، ثم أحرف أو أرقام أو شرطات سفلية (بحد أقصى 40 حرفًا).`,
        );
      if (keys.has(track.key)) errors.push(`${label}: اختر معرّف رابط فريدًا للمسار.`);
      keys.add(track.key);
      if (!track.name.trim() || track.name.length > 120)
        errors.push(`${label}: أدخل اسم عرض بحد أقصى 120 حرفًا.`);
      if (!integer(track.weeklyMeetings, 1, 7) || track.meetings !== track.weeklyMeetings * 4)
        errors.push(
          `${label}: اختر من 1 إلى 7 حصص أسبوعيًا؛ يُحسب إجمالي الأسابيع الأربعة تلقائيًا.`,
        );
      if (
        ![track.durationMinutes, track.maxDurationMinutes, track.trialDurationMinutes].every((n) =>
          integer(n, 30, 120),
        )
      )
        errors.push(`${label}: يجب أن تكون مدة الدرس والتجربة من 30 إلى 120 دقيقة كاملة.`);
      if (
        track.maxDurationMinutes < track.durationMinutes ||
        (track.model !== 'quran' && track.maxDurationMinutes !== track.durationMinutes)
      )
        errors.push(
          `${label}: تحتاج الدروس الدراسية إلى مدة ثابتة؛ ويجب ألا يقل أقصى وقت للقرآن عن الحد الأدنى.`,
        );
      if ((offer.category === 'quran') !== (track.model === 'quran'))
        errors.push(`${label}: افصل دروس القرآن والدروس الدراسية في خطط مستقلة.`);
      if (
        track.subjectIds.length < 1 ||
        track.subjectIds.length > 20 ||
        (track.model !== 'generalist' && track.subjectIds.length !== 1)
      )
        errors.push(
          `${label}: ${track.model === 'generalist' ? 'اختر من 1 إلى 20 مادة' : 'اختر مادة واحدة فقط'}.`,
        );
      if (catalog) {
        const selected = track.subjectIds.map((id) => catalog.subjects.find((subject) => subject.id === id));
        if (selected.some((subject) => !subject)) errors.push(`${label}: اختر مواد متاحة من القائمة.`);
        if (selected.some((subject) => subject && (subject.slug === 'quran') !== (offer.category === 'quran')))
          errors.push(`${label}: افصل القرآن في خطة مستقلة عن المواد الدراسية.`);
        if (track.model === 'generalist' && selected.some((subject) => subject?.slug === 'english'))
          errors.push(`${label}: أضف الإنجليزية في مسار مستقل لمعلّم متخصص.`);
      }
      if (!integer(track.changeAllowance, 0, 10))
        errors.push(`${label}: اسمح بما بين 0 و10 تعديلات للطالب في كل دورة.`);
      if (!track.compensation.trim() || track.compensation.length > 1000)
        errors.push(`${label}: أدخل شروط مستحقات المعلّم بحد أقصى 1,000 حرف.`);
    }
  }
  if (step === 2) {
    const p = offer.policy;
    if (p.cycleDays !== 28) errors.push('يجب أن تغطي الخطط أربعة أسابيع (28 يومًا).');
    if (!integer(p.holdHours, 1, 168)) errors.push('يجب أن تكون مهلة الحجز المؤقت للجدول من 1 إلى 168 ساعة.');
    if (!integer(p.noticeHours, 1, 168)) errors.push('يجب أن تكون مهلة إشعار تعديل الطالب من 1 إلى 168 ساعة.');
    if (!integer(p.makeupDays, 0, 30)) errors.push('يجب أن تكون مهلة التعويض من 0 إلى 30 يومًا.');
    if (!integer(p.reminderDays, 1, 27)) errors.push('يجب أن يكون تذكير التجديد من 1 إلى 27 يومًا.');
    if (!integer(p.retentionHours, 1, 648)) errors.push('يجب أن تكون مهلة الاحتفاظ بالمواعيد للتجديد من 1 إلى 648 ساعة.');
    if (offer.trialPolicy && !integer(offer.trialPolicy.eligibilityLimit, 1, 2147483647))
      errors.push('أدخل عددًا صحيحًا موجبًا للتجارب التعريفية المسموح بها.');
  }
  return errors;
}

export function offerPublicationErrors(offer: PlanOfferInput): string[] {
  const errors: string[] = [];
  if (!Number.isInteger(offer.priceCents) || offer.priceCents <= 0)
    errors.push('حدّد سعرًا للخطة أكبر من صفر قبل النشر.');
  if (!Number.isInteger(offer.trialPriceCents) || offer.trialPriceCents <= 0)
    errors.push('حدّد سعرًا للتجربة أكبر من صفر قبل النشر؛ التجربة مدفوعة.');
  if (offer.trialPriceCents > offer.priceCents)
    errors.push('لا يمكن أن يتجاوز سعر التجربة سعر الخطة.');
  for (const track of offer.tracks)
    if (!track.compensation.trim())
      errors.push(`المسار «${track.name}»: حدّد شروط مستحقات المعلّم قبل النشر.`);
  return errors;
}

export function offerPriceErrors(offer: PlanOfferInput): string[] {
  const errors: string[] = [];
  if (![offer.priceCents, offer.trialPriceCents].every((value) => Number.isInteger(value) && value >= 0 && value <= 100000000))
    errors.push('أدخل أسعارًا من 0 إلى 1,000,000 وبحد أقصى منزلتين عشريتين.');
  if (offer.trialPriceCents > offer.priceCents) errors.push('لا يمكن أن يتجاوز سعر التجربة سعر الخطة.');
  return errors;
}
